import type {
  ConnectorCloneRequest,
  ConnectorHealthResponse,
  EpayoutsCatalogResponse,
  PanesCatalogResponse,
  VaultRoutesApplyRequest,
  VaultRoutesApplyResponse,
  VaultRoutesPreviewRequest,
  VaultRoutesPreviewResponse,
  VaultVerificationResponse,
  VaultVerifyRequest,
  ConnectorCreateRequest,
  ConnectorResponse,
  ConnectorUpdateRequest,
  ConnectorWebhookListResponse,
  ConnectorWebhookRegisterRequest,
  ConnectorWebhookRegisterResponse,
  ConnectorWebhookSyncResponse,
  AirwallexApplePayDomainsRegisterRequest,
  AirwallexApplePayDomainsRegisterResponse,
  StripePaymentMethodDomainsRegisterRequest,
  StripePaymentMethodDomainsRegisterResponse,
} from '../types';
import type { RequestFn } from '../client';

export class Connectors {
  constructor(private readonly request: RequestFn) {}

  async create(accountId: string, params: ConnectorCreateRequest): Promise<ConnectorResponse> {
    return this.request('POST', `/account/${encodeURIComponent(accountId)}/connectors`, {
      body: params,
    });
  }

  /**
   * One connector account.
   *
   * The credential-bearing fields come back `null` here, whatever is stored:
   * `connector_webhook_details`, `connector_wallets_details`,
   * `pm_auth_config` and `additional_merchant_data`. They are dropped rather
   * than masked, because an editor that prefills from this response and
   * PATCHes the field back would otherwise save a mask over a live signing
   * secret. Send those fields only when the operator has typed a new value,
   * and omit them entirely otherwise — an omitted field leaves the stored one
   * alone.
   *
   * This is the retrieve path alone. `create` and `update` echo back what the
   * caller sent, and `clone` returns the *copied* secrets — see that method.
   *
   * Because the value is gone, `has_live_webhook_secret` and
   * `has_sandbox_webhook_secret` are what tell a stored webhook signing secret
   * from an unconfigured one. Render those; never infer configuration from the
   * `null` block.
   *
   * `GET /account/{accountId}/connectors/{connectorId}`
   */
  async retrieve(accountId: string, connectorId: string): Promise<ConnectorResponse> {
    return this.request(
      'GET',
      `/account/${encodeURIComponent(accountId)}/connectors/${encodeURIComponent(connectorId)}`,
    );
  }

  /**
   * The merchant's connector accounts.
   *
   * Never wider than the caller: an API key pinned to one shop lists that
   * shop's connectors only, not every sibling shop's.
   *
   * `GET /account/{accountId}/connectors`
   */
  async list(accountId: string): Promise<ConnectorResponse[]> {
    return this.request('GET', `/account/${encodeURIComponent(accountId)}/connectors`);
  }

  /**
   * The profile-scoped connector list. The merchant-wide `list()` is
   * merchant-gated and 403s for a profile-entity (shop user) JWT; this
   * variant is scoped server-side to the caller's own profile.
   *
   * `GET /account/{accountId}/profile/connectors`
   */
  async listByProfile(accountId: string): Promise<ConnectorResponse[]> {
    return this.request('GET', `/account/${encodeURIComponent(accountId)}/profile/connectors`);
  }

  /**
   * Every connector's pane capabilities, as the router itself knows them —
   * which rails a connector supports, and which methods it can publish as a
   * pane with the confirm routing keys each one carries.
   *
   * Capability facts only: no label, sublabel or category comes back. A
   * designer renders a method's copy from its own locale files, keyed by
   * `key`, and takes `payment_method` / `payment_method_type` from here
   * verbatim rather than deriving them client-side.
   *
   * This is what the pane helpers in `src/panes.ts` should be driven from —
   * `paneMethodInfo`, `defaultPane` and `validatePanes` all take a
   * {@link PanesConnectorCatalog} out of this response, so a connector the
   * router starts publishing panes for needs no SDK release.
   *
   * `GET /account/{accountId}/connectors/native-panes/catalog`
   *
   * The path keeps the `native-panes` spelling deliberately. The identifiers
   * were renamed off "native pane"; the route was not, and pointing at the
   * backend's new name before it is deployed everywhere would both 404 against
   * un-upgraded replicas mid-rollout and fail `scripts/parity-check.mjs`,
   * which blocks publishing.
   */
  async getPanesCatalog(accountId: string): Promise<PanesCatalogResponse> {
    return this.request(
      'GET',
      `/account/${encodeURIComponent(accountId)}/connectors/native-panes/catalog`,
    );
  }

  /** @deprecated Renamed to {@link getPanesCatalog}. Removed in 0.112.0. */
  async getNativePanesCatalog(accountId: string): Promise<PanesCatalogResponse> {
    return this.getPanesCatalog(accountId);
  }

  /**
   * Sweep the merchant's own e-Payouts module and return the rails it
   * actually has enabled. Server-side this makes many upstream calls, so it
   * can take several seconds — show progress.
   *
   * `POST /account/{accountId}/connectors/{connectorId}/epayouts/catalog/sync`
   */
  async syncEpayoutsCatalog(
    accountId: string,
    connectorId: string,
  ): Promise<EpayoutsCatalogResponse> {
    return this.request(
      'POST',
      `/account/${encodeURIComponent(accountId)}/connectors/${encodeURIComponent(connectorId)}/epayouts/catalog/sync`,
    );
  }

  /**
   * Update a connector account.
   *
   * `connector_webhook_details` **merges** into the stored block key by key:
   * an absent key keeps its stored secret, an explicit value is written, and
   * an explicit empty string clears that secret. Send only the
   * keys the operator typed — padding the other environment's keys with `''`
   * clears a live signing secret and inbound webhooks stop verifying.
   *
   * The other credential-bearing fields — `connector_wallets_details`,
   * `pm_auth_config`, `additional_merchant_data` — are still whole-value
   * replacements: omit them unless you are writing a complete new value.
   *
   * `POST /account/{accountId}/connectors/{connectorId}`
   */
  async update(
    accountId: string,
    connectorId: string,
    params: ConnectorUpdateRequest,
  ): Promise<ConnectorResponse> {
    return this.request(
      'POST',
      `/account/${encodeURIComponent(accountId)}/connectors/${encodeURIComponent(connectorId)}`,
      {
        body: params,
      },
    );
  }

  /**
   * Remove a connector account.
   *
   * A shop-scoped role may remove a connector of its own shop — the shop is
   * re-checked server-side — so creating processors and removing them are the
   * same rung of access rather than two.
   *
   * `DELETE /account/{accountId}/connectors/{connectorId}`
   */
  async delete(accountId: string, connectorId: string): Promise<ConnectorResponse> {
    return this.request(
      'DELETE',
      `/account/${encodeURIComponent(accountId)}/connectors/${encodeURIComponent(connectorId)}`,
    );
  }

  /**
   * Clone a connector into another shop (business profile) of the same
   * merchant. `POST /account/{accountId}/connectors/{connectorId}/clone`
   *
   * Credentials are copied server-side, re-encrypted under the same merchant
   * key, so the caller never has to *supply* them — `retrieve` returns `null`
   * for the credential fields, which is what makes a client-side copy
   * impossible in the first place.
   *
   * The response, however, is the unredacted connector: `connector_account_details`
   * is masked, but `connector_webhook_details`, `connector_wallets_details`,
   * `pm_auth_config` and `additional_merchant_data` come back with the copied
   * secrets in them — values this caller never sent. Do not log or echo the
   * response; read `merchant_connector_id` and discard the rest.
   */
  async clone(
    accountId: string,
    connectorId: string,
    params: ConnectorCloneRequest,
  ): Promise<ConnectorResponse> {
    return this.request(
      'POST',
      `/account/${encodeURIComponent(accountId)}/connectors/${encodeURIComponent(connectorId)}/clone`,
      { body: params },
    );
  }

  // --- Advanced operations (Task 4.8) ---

  /**
   * Run the configuration checks for a vault (VGS) connector account:
   * credential validity, write-only Collect scope, reachability, environment
   * coherence, route coverage. Read-only but not cheap — it decrypts the
   * vault's management credential and talks to VGS.
   *
   * `POST /account/{accountId}/connectors/{connectorId}/vault/verify`
   */
  async verifyVault(
    accountId: string,
    connectorId: string,
    params: VaultVerifyRequest,
  ): Promise<VaultVerificationResponse> {
    return this.request(
      'POST',
      `/account/${encodeURIComponent(accountId)}/connectors/${encodeURIComponent(connectorId)}/vault/verify`,
      { body: params },
    );
  }

  /**
   * Compute the route document the vault SHOULD have and diff it against
   * what exists, without writing anything. The returned fingerprints must be
   * echoed byte for byte on {@link Connectors.applyVaultRoutes}.
   *
   * A router without these endpoints answers 404 — render that as "this
   * build cannot configure routes", never as "there is nothing to change".
   *
   * `POST /account/{accountId}/connectors/{connectorId}/vault/routes/preview`
   */
  async previewVaultRoutes(
    accountId: string,
    connectorId: string,
    params: VaultRoutesPreviewRequest,
  ): Promise<VaultRoutesPreviewResponse> {
    return this.request(
      'POST',
      `/account/${encodeURIComponent(accountId)}/connectors/${encodeURIComponent(connectorId)}/vault/routes/preview`,
      { body: params },
    );
  }

  /**
   * Write the routes the merchant just previewed. Both fingerprints come
   * from the preview and are opaque: `expected_current_fingerprint` says the
   * vault has not moved (`null` = "the preview found no routes" and is sent
   * as `null`, never omitted), `expected_desired_fingerprint` says the
   * document is still the one on screen. A 409 (`DE_04`) means the vault
   * changed since the preview — nothing was written; preview again.
   *
   * `POST /account/{accountId}/connectors/{connectorId}/vault/routes/apply`
   */
  async applyVaultRoutes(
    accountId: string,
    connectorId: string,
    params: VaultRoutesApplyRequest,
  ): Promise<VaultRoutesApplyResponse> {
    return this.request(
      'POST',
      `/account/${encodeURIComponent(accountId)}/connectors/${encodeURIComponent(connectorId)}/vault/routes/apply`,
      { body: params },
    );
  }

  /**
   * What the processor says about this connector account: whether it will
   * accept charges, which capabilities are live, and anything it is still
   * waiting on.
   *
   * This is **not** {@link Connectors.verify}. That one asks whether the stored
   * credentials work, by running a test-card authorization; a processor can
   * revoke an account's ability to take payments while the credentials stay
   * perfectly valid, and then a credential check reports healthy while every
   * checkout silently stalls. This asks the account's own status instead, and
   * does not create a payment. By default it reads a scheduler snapshot;
   * `refresh: true` asks for a new probe and `stale` identifies an overdue result.
   * `test_mode` selects the requested environment; `credential_source` identifies
   * a sandbox, live or shared credential set. Not every provider reports chargeability.
   *
   * Connectors with no probe answer `state: 'unknown'` with
   * `unknown_reason: 'connector_not_supported'`, rather than an error — every
   * `'unknown'` carries a reason code you render your own words for. A router
   * without this endpoint answers 404 — render that as "this build cannot
   * report health", never as "healthy".
   *
   * `GET /account/{accountId}/connectors/{connectorId}/health`
   */
  async health(
    accountId: string,
    connectorId: string,
    options?: { test_mode?: boolean; refresh?: boolean },
  ): Promise<ConnectorHealthResponse> {
    return this.request(
      'GET',
      `/account/${encodeURIComponent(accountId)}/connectors/${encodeURIComponent(connectorId)}/health`,
      { query: options },
    );
  }

  /** Verify connector credentials. `POST /account/connectors/verify` */
  async verify(params: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.request('POST', '/account/connectors/verify', { body: params });
  }

  /**
   * Register a webhook for a connector.
   * `POST /account/{merchantId}/connectors/webhooks/{connectorId}`
   *
   * @param params - Optional event scope. Defaults to `{ event_type: 'all_events' }`
   *   when omitted; pass `{ event_type: { specific_event: '…' } }` to scope
   *   to a single event.
   */
  async registerWebhook(
    merchantId: string,
    connectorId: string,
    params?: ConnectorWebhookRegisterRequest,
  ): Promise<ConnectorWebhookRegisterResponse> {
    const path = `/account/${encodeURIComponent(merchantId)}/connectors/webhooks/${encodeURIComponent(connectorId)}`;
    if (params === undefined) return this.request('POST', path);
    return this.request('POST', path, { body: params });
  }

  /**
   * Register checkout/shop domains as Stripe payment method domains, so Apple
   * Pay renders on those pages.
   * `POST /account/{merchantId}/connectors/{connectorId}/stripe/payment-method-domains`
   *
   * Stripe connectors only. One call registers against a single credential set
   * (`environment`, default `'live'`) — call twice to cover live and sandbox.
   * Per-URL outcomes come back in `results`; a missing sandbox credential set
   * is a request-level 400.
   */
  async registerStripePaymentMethodDomains(
    merchantId: string,
    connectorId: string,
    params: StripePaymentMethodDomainsRegisterRequest,
  ): Promise<StripePaymentMethodDomainsRegisterResponse> {
    return this.request(
      'POST',
      `/account/${encodeURIComponent(merchantId)}/connectors/${encodeURIComponent(connectorId)}/stripe/payment-method-domains`,
      { body: params },
    );
  }

  /**
   * Register checkout/shop domains as Airwallex Apple Pay domains, so Apple Pay
   * renders on those pages.
   * `POST /account/{merchantId}/connectors/{connectorId}/airwallex/apple-pay-domains`
   *
   * Airwallex connectors only. One call registers against a single credential
   * set (`environment`, default `'live'`) — call twice to cover live and
   * sandbox. Idempotent: a host already on the account comes back
   * `already_registered` without a write.
   */
  async registerAirwallexApplePayDomains(
    merchantId: string,
    connectorId: string,
    params: AirwallexApplePayDomainsRegisterRequest,
  ): Promise<AirwallexApplePayDomainsRegisterResponse> {
    return this.request(
      'POST',
      `/account/${encodeURIComponent(merchantId)}/connectors/${encodeURIComponent(connectorId)}/airwallex/apple-pay-domains`,
      { body: params },
    );
  }

  /** Get registered webhooks for a connector. `GET /account/{merchantId}/connectors/webhooks/{connectorId}` */
  async getWebhook(merchantId: string, connectorId: string): Promise<ConnectorWebhookListResponse> {
    return this.request(
      'GET',
      `/account/${encodeURIComponent(merchantId)}/connectors/webhooks/${encodeURIComponent(connectorId)}`,
    );
  }

  /**
   * Bring an already-registered webhook's event subscription up to date with
   * the events Delopay handles.
   * `POST /account/{merchantId}/connectors/webhooks/{connectorId}/sync-events`
   *
   * A PSP freezes an endpoint's event list at registration time, so an endpoint
   * created before an event type was added never receives it — silently, with
   * no error anywhere. {@link getWebhook} reports the gap as `missing_events`;
   * this repairs it.
   *
   * Unlike re-registering, the endpoints are updated in place: the endpoint id
   * and its signing secret are preserved, so signature verification keeps
   * working across the change. Stripe connectors only; idempotent, so it is
   * safe to call on a schedule or after every deploy.
   *
   * @example
   * ```typescript
   * const sync = await delopay.connectors.syncWebhookEvents('mer_abc', 'mca_xyz');
   * for (const endpoint of sync.endpoints) {
   *   if (endpoint.error_message) console.warn(endpoint.connector_webhook_id, endpoint.error_message);
   *   else if (endpoint.updated) console.log('subscribed', endpoint.added_events);
   * }
   * ```
   */
  async syncWebhookEvents(
    merchantId: string,
    connectorId: string,
  ): Promise<ConnectorWebhookSyncResponse> {
    return this.request(
      'POST',
      `/account/${encodeURIComponent(merchantId)}/connectors/webhooks/${encodeURIComponent(connectorId)}/sync-events`,
    );
  }

  /** List available payment methods. `GET /account/payment-methods` */
  async listPaymentMethods(): Promise<Record<string, unknown>[]> {
    return this.request('GET', '/account/payment-methods');
  }
}
